import { createWorker } from "tesseract.js";

const pushSearchValue = (searchKeysSet, value) => {
    if (value === null || value === undefined) return;
    const trimmed = String(value).trim();
    // Skip empty, single-character, or symbol-only values (e.g. "%", "g", "<").
    // These substring-match across the document and flood it with false highlights.
    if (trimmed.length < 2 || !/[a-z0-9]/i.test(trimmed)) return;
    searchKeysSet.add(trimmed);
};

const handleExtractionTables = (searchKeysSet, entity_tables = []) => {
    for (const entity_table of entity_tables) {
        const rows = entity_table?.rows ?? [];
        for (const row of rows) {
            Object.entries(row).forEach(([key, val]) => {
                // Skip helper columns, normalized values, and the short "unit" column —
                // none appear as meaningful standalone tokens worth highlighting.
                if (key === "_unmapped_columns" || key === "unit" || key.endsWith("_mapped")) return;
                pushSearchValue(searchKeysSet, val);
            });
        }
    }
};

const handleExtractionGroups = (searchKeysSet, entity_groups = []) => {
    for (const entity_group of entity_groups) {
        const groupFields = entity_group.fields ?? [];

        for (const groupField of groupFields) {
            if (groupField?.data_absent === false) {
                // Prefer the verbatim source text from the document over the normalized value.
                pushSearchValue(searchKeysSet, groupField?.source_value ?? groupField?.value);
            }
        }

        handleExtractionTables(searchKeysSet, entity_group.tables ?? []);
    }
};

export const generateSearchKeysNew = (entities) => {
    const searchKeysSet = new Set();

    const entityFields = entities?.mapped_fields ?? {};
    const entityTables = entities?.tables ?? [];
    const entityGroups = entities?.groups ?? [];

    for (const field of Object.values(entityFields)) {
        if (field?.source_label && field?.data_absent === false) {
            // Prefer the verbatim source text from the document over the normalized value.
            pushSearchValue(searchKeysSet, field?.source_value ?? field?.value);
        }
    }

    handleExtractionTables(searchKeysSet, entityTables);
    handleExtractionGroups(searchKeysSet, entityGroups);

    return Array.from(searchKeysSet);
};

export const addProgrammaticAnnotation = async (entity, coordinates, page) => {
    const newAnnotation = {
        id: Date.now() + Math.random(),
        type: 'programmatic',
        polygon: coordinates,
        label: entity.label,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        text: entity.text,
        page,
    };
    return newAnnotation;
}

export const initTesseractWorker = async () => {
    let tesseractWorker = null;
    if (!tesseractWorker) {
        tesseractWorker = createWorker({
            workerPath: 'https://unpkg.com/tesseract.js@v2.1.5/dist/worker.min.js',
            langPath: 'https://tessdata.projectnaptha.com/4.0.0',
            corePath: 'https://unpkg.com/tesseract.js-core@2.1.0/tesseract-core.wasm.js',
        });

        await tesseractWorker.load();
        await tesseractWorker.loadLanguage('eng');
        await tesseractWorker.initialize('eng');
    }
    return tesseractWorker;
};

export const preprocessCanvas = (canvas) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    const blockSize = 15;
    const C = 10;
    const width = canvas.width;
    const height = canvas.height;

    const grayData = new Uint8ClampedArray(width * height);
    for (let i = 0; i < data.length; i += 4) {
        grayData[i / 4] = 0.2989 * data[i] + 0.5870 * data[i + 1] + 0.1140 * data[i + 2];
    }

    const thresholdData = new Uint8ClampedArray(width * height);
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            let sum = 0;
            let count = 0;
            for (let dy = -blockSize; dy <= blockSize; dy++) {
                for (let dx = -blockSize; dx <= blockSize; dx++) {
                    const nx = x + dx;
                    const ny = y + dy;
                    if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                        sum += grayData[ny * width + nx];
                        count++;
                    }
                }
            }
            const mean = sum / count;
            const pixel = grayData[y * width + x];
            thresholdData[y * width + x] = pixel > mean - C ? 255 : 0;
        }
    }

    for (let i = 0; i < data.length; i += 4) {
        const gray = thresholdData[i / 4];
        data[i] = data[i + 1] = data[i + 2] = gray;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
};

export const performOCR = async (page, renderTaskRef, scale, viewport) => {
    let worker = null;
    try {
        const ocrScale = scale * 2;
        const ocrScaleFactor = scale / ocrScale;
        const ocrViewport = page.getViewport({ scale: ocrScale });
        const offscreenCanvas = document.createElement('canvas');
        offscreenCanvas.width = ocrViewport.width;
        offscreenCanvas.height = ocrViewport.height;
        const context = offscreenCanvas.getContext('2d');

        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
            renderTaskRef.current = null;
        }

        renderTaskRef.current = page.render({ canvasContext: context, viewport: ocrViewport });
        await renderTaskRef.current.promise;
        renderTaskRef.current = null;

        const processedCanvas = preprocessCanvas(offscreenCanvas);
        worker = await initTesseractWorker();
        await worker.setParameters({
            tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.,/ ',
            preserve_interword_spaces: 1,
            tessedit_pageseg_mode: 6,
        });

        const { data } = await worker.recognize(processedCanvas);

        let textItems = [];
        if (data.words?.length > 0) {
            const mergedWords = [];
            let currentPhrase = null;

            data.words
                .filter(word => word.text?.trim() && word.confidence > 60)
                .sort((a, b) => {
                    const ay = a.bbox.y0;
                    const by = b.bbox.y0;
                    if (Math.abs(ay - by) > 5) return ay - by;
                    return a.bbox.x0 - b.bbox.x0;
                })
                .forEach((word, index, words) => {
                    const { x0, y0, x1, y1 } = word.bbox;
                    const scaledX = x0 * ocrScaleFactor;
                    const scaledY = y0 * ocrScaleFactor;
                    const scaledWidth = (x1 - x0) * ocrScaleFactor;
                    const scaledHeight = (y1 - y0) * ocrScaleFactor;

                    const isCloseToPrevious =
                        index > 0 &&
                        Math.abs(y0 - words[index - 1].bbox.y0) < 5 &&
                        Math.abs(x0 - words[index - 1].bbox.x1) < scaledWidth * 0.5;

                    if (isCloseToPrevious && currentPhrase) {
                        currentPhrase.text += ` ${word.text}`;
                        currentPhrase.bbox.x1 = x1;
                        currentPhrase.bbox.y1 = Math.max(currentPhrase.bbox.y1, y1);
                        currentPhrase.confidence = Math.min(currentPhrase.confidence, word.confidence);
                    } else {
                        if (currentPhrase) {
                            mergedWords.push(currentPhrase);
                        }
                        currentPhrase = {
                            text: word.text,
                            bbox: { x0, y0, x1, y1 },
                            confidence: word.confidence,
                        };
                    }
                });

            if (currentPhrase) {
                mergedWords.push(currentPhrase);
            }

            textItems = mergedWords.map(phrase => {
                const { x0, y0, x1, y1 } = phrase.bbox;
                const x = x0 * ocrScaleFactor;
                const y = y0 * ocrScaleFactor;
                const width = (x1 - x0) * ocrScaleFactor;
                const height = (y1 - y0) * ocrScaleFactor;
                return {
                    str: phrase.text,
                    x,
                    y,
                    width,
                    height,
                    fontName: 'unknown',
                    dir: 'ltr',
                    confidence: phrase.confidence,
                };
            });
        }
        return textItems;
    } catch (error) {
        console.error('OCR failed:', error);
        return [];
    } finally {
        if (worker) {
            await worker.terminate();
        }
    }
};


export const documentStyles = `
/* =========================
   Base document styles
========================= */
.document-content {
  font-family: 'Segoe UI', 'Calibri', Arial, sans-serif;
  line-height: 1.4;
  color: #1d1d1f;
  background: #fafafa;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  position: relative;
  min-height: 100vh;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}

/* =========================
   Word (docx) styles
========================= */
.document-content.docx p {
  margin: 0 0 10pt;
  font-size: 11pt;
  text-align: left;
  line-height: 1.4;
}

.document-content.docx h1,
.document-content.docx h2,
.document-content.docx h3 {
  color: #2F5496;
  margin: 12pt 0 6pt;
  font-weight: bold;
}

.document-content.docx h1 { font-size: 18pt; }
.document-content.docx h2 { font-size: 14pt; }
.document-content.docx h3 { font-size: 12pt; color: #1F4E79; }

/* Tables inside Word */
.document-content.docx table {
  border-collapse: collapse;
  width: 100%;
  margin: 12pt 0;
  font-size: 10.5pt;
  border: 1px solid #d9d9d9;
  background-color: #ffffff;
}

.document-content.docx th,
.document-content.docx td {
  border: 1px solid #e0e0e0;
  padding: 6pt 10pt;
  vertical-align: middle;
  text-align: left;
}

.document-content.docx th {
  background-color: #f4f4f4;
  font-weight: bold;
  text-align: center;
}

/* Lists */
.document-content.docx ul,
.document-content.docx ol {
  margin: 10pt 0;
  padding-left: 28pt;
}

.document-content.docx li {
  margin: 4pt 0;
  line-height: 1.3;
}

/* Header/Footer */
.document-content.docx .header,
.document-content.docx .footer {
  border-top: 1px solid #ddd;
  padding-top: 6pt;
  margin-top: 16pt;
  font-size: 9pt;
  color: #666;
  text-align: center;
}

/* =========================
   Excel (xlsx) styles
========================= */
.document-content.xlsx {
  display: flex;
  flex-direction: column;
  background: #f9f9f9;
}

.document-content.xlsx .excel-table-wrapper {
  position: relative;
  background: #ffffff;
  border: 1px solid #dcdcdc;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  overflow: auto;
}

.document-content.xlsx table {
  border-collapse: collapse;
  font-size: 10.5pt;
  font-family: inherit;
  background: #ffffff;
}

/* Table headers */
.document-content.xlsx th.column-header,
.document-content.xlsx th.row-header {
  background: #f2f2f2;
  font-weight: 600;
  border: 1px solid #dadada;
  text-align: center;
  padding: 4px 6px;
  position: sticky;
  z-index: 2;
}

.document-content.xlsx th.column-header { top: 0; }
.document-content.xlsx th.row-header { left: 0; }

/* Corner header */
.document-content.xlsx th.corner {
  background: #e1e1e1;
  border: 1px solid #d3d3d3;
  position: sticky;
  top: 0; left: 0;
  z-index: 3;
}

.corner, .row-header {
    width: 50px; /* Or whatever you choose for the header width */
    min-width: 50px;
    max-width: 50px;
    box-sizing: border-box;
}


/* Cells */
.document-content.xlsx td {
  border: 1px solid #e1e1e1;
  padding: 6px 8px;
  vertical-align: middle;
  text-align: left;
  height: 24px;
  background: #ffffff;
}

.document-content.xlsx td.merged {
  border: none;
  background: transparent;
}

/* Sheet Tabs */
.excel-tabs {
  display: flex;
  align-items: stretch;
  height: 38px;
  background: #f3f3f3;
  border-top: 1px solid #c7c7c7;
}

.excel-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  max-width: 140px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  color: #5a5a5a;
  cursor: pointer;
  background: #f3f3f3;
  border: 1px solid #c7c7c7;
  border-bottom: none;
  margin-right: 2px;
  border-radius: 6px 6px 0 0;
  transition: all 0.2s ease;
}

.excel-tab.active {
  background: #ffffff;
  color: #222;
  border-top: 2px solid #0078d4;
  z-index: 2;
}

.excel-tab:hover {
  background: #e7e7e7;
}

`